import { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { ActionOverlayPopover, Card, DragDropV2Icon, Layout, Pill, ShuffleIcon } from '@utilities';

import Question from '../Question';

import styles from './_index.module.scss';

// Temporarily use inactive draggable props for sub-questions within the monadic block to prevent drag and drop functionality
const inactiveDraggableProvided = {
  draggableProps: {},
  dragHandleProps: {},
  innerRef: () => {},
};

const getImageUrl = (image) => image?.cdnUrl || image?.url;

const SequentialMonadicBlock = ({
  canPreviewSurvey,
  firstErrorRef,
  isDetailedQuestionView,
  onEditQuestion,
  onPreviewSurvey,
  provided,
  question,
  questionLogic,
  selectedQuestions,
  setQuestionLogic,
  showActions,
}) => {
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const activeConcepts = (question.concepts || []).filter(({ _delete }) => !_delete);
  const activeSubQuestions = (question.subQuestions || []).filter(({ _delete }) => !_delete);
  const conceptCount = activeConcepts.length;
  const questionCount = activeSubQuestions.length;
  const questionCountLabel = `${questionCount} question${questionCount === 1 ? '' : 's'}`;
  const optionsDropdown = useRef(null);
  const randomizationLabel = question.properties?.randomizeSubQuestions ? 'Randomized. ' : '';

  return (
    <div
      className={styles['sequential-monadic-draggable-container']}
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
    >
      <Card
        as={Layout.Flex.Column}
        className={styles['sequential-monadic-card']}
        elevation={2}
        gap="small"
      >
        <div className={styles['sequential-monadic-header']}>
          <div className={styles['sequential-monadic-title-wrapper']}>
            <div className={styles['sequential-monadic-drag-handle']}>
              <DragDropV2Icon />
            </div>
            <h3>Monadic Section</h3>
            <ShuffleIcon />
            <Pill isCompleted value={questionCountLabel} />
          </div>
          {showActions && (
            <div className={styles['sequential-monadic-actions']}>
              <ActionOverlayPopover
                hasArrow={false}
                isActive={isDropdownActive}
                isDisabled
                onClick={() => setIsDropdownActive(!isDropdownActive)}
                onHide={() => setIsDropdownActive(false)}
                target={optionsDropdown.current}
              ></ActionOverlayPopover>
            </div>
          )}
        </div>

        <p className={styles['sequential-monadic-copy']}>
          This section of questions will show the following concepts.
        </p>
        <p className={styles['sequential-monadic-copy']}>
          {randomizationLabel}Panelists will see all of these questions {conceptCount} time
          {conceptCount === 1 ? '' : 's'} based on the following concepts:
        </p>

        {activeConcepts.length > 0 && (
          <ol className={styles['sequential-monadic-concepts']}>
            {activeConcepts.map((concept, index) => {
              const imageUrl = getImageUrl(concept.image);
              const conceptName = concept.name || `Concept ${index + 1}`;

              return (
                <li className={styles['sequential-monadic-concept']} key={concept.id ?? index}>
                  <div className={styles['sequential-monadic-concept-image-wrapper']}>
                    {imageUrl && (
                      <img
                        alt={conceptName}
                        className={styles['sequential-monadic-concept-image']}
                        src={imageUrl}
                      />
                    )}
                  </div>
                  <span className={styles['sequential-monadic-concept-name']}>{conceptName}</span>
                </li>
              );
            })}
          </ol>
        )}

        <div className={styles['sequential-monadic-questions']}>
          {activeSubQuestions.map((subQuestion, index) => (
            <Question
              canPreviewSurvey={canPreviewSurvey}
              firstErrorRef={firstErrorRef}
              index={index}
              isDetailedQuestionView={isDetailedQuestionView}
              key={subQuestion.id ?? index}
              number={index + 1}
              onEditQuestion={onEditQuestion}
              onPreviewSurvey={onPreviewSurvey}
              provided={inactiveDraggableProvided}
              question={subQuestion}
              questionLogic={questionLogic || {}}
              selectedQuestions={selectedQuestions}
              setQuestionLogic={setQuestionLogic}
              showActions={false}
            />
          ))}
        </div>
      </Card>
    </div>
  );
};

SequentialMonadicBlock.propTypes = {
  canPreviewSurvey: PropTypes.bool.isRequired,
  firstErrorRef: PropTypes.object.isRequired,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  onEditQuestion: PropTypes.func.isRequired,
  onPreviewSurvey: PropTypes.func.isRequired,
  provided: PropTypes.object,
  question: PropTypes.object.isRequired,
  questionLogic: PropTypes.object,
  selectedQuestions: PropTypes.array.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
  showActions: PropTypes.bool.isRequired,
};

export default SequentialMonadicBlock;
